import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';

interface Ops {
  ref?: React.MutableRefObject<HTMLElement | null> | null;
  debounce?: number;
  onlyInFullView: boolean;
}

const defaultOps: Ops = {
  ref: null,
  debounce: 150,
  onlyInFullView: false,
};

/**
 * Primarily for support of mobile safari (vs IntersectionObserver)
 *
 * can `ref={getInView}` or pass a ref to hooks-options
 */
export default function useInViewport(ops: Ops = defaultOps) {
  const [node, getInView] = useState<HTMLElement | null>(
    ops.ref?.current || null
  );
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (ops.ref?.current) getInView(ops.ref?.current);
  }, [ops.ref?.current]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (node) {
      const cb = debounce(() => {
        const rect = node.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom;

        let isVisible = false;

        if (ops.onlyInFullView) {
          // Only completely visible
          isVisible = elemTop >= 0 && elemBottom <= window.innerHeight;
        } else {
          // Partially visible
          isVisible = elemTop < window.innerHeight && elemBottom >= 0;
        }
        return setInView(isVisible);
      }, ops.debounce);

      document.addEventListener('scroll', cb, { passive: true });
      return () => document.removeEventListener('scroll', cb);
    }
  }, [node]); // eslint-disable-line react-hooks/exhaustive-deps

  return { getInView, inView };
}
